import React, { useState } from "react";
import {
  MapPinIcon,
  EnvelopeIcon,
  PhoneIcon,
  CheckIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/outline";
import ContactImg from "../../../assets/home-aboutus-1a.jpg";
import Eyebrow from "../../../shared/design/Eyebrow";

// Mirrors the "INTERESTED IN" options in the Contact.dc.html mockup.
const INTERESTS = [
  "A specific tour",
  "Custom / private expedition",
  "Group & corporate trips",
  "Press & partnerships",
  "Something else",
] as const;

const infoLabelClass =
  "mb-1 font-mono text-[12px] font-bold tracking-[0.06em] text-primary-500";
const infoValueClass = "text-[14.5px] leading-snug text-white/95";
const fieldLabelClass =
  "mb-[7px] block text-[12px] font-bold tracking-[0.04em] text-muted";
const socialClass =
  "flex h-10 w-10 items-center justify-center rounded-full border border-white/35 text-[13px] transition-colors hover:border-primary-500 hover:text-primary-500";

const Contact = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState<string>(INTERESTS[0]);
  const [message, setMessage] = useState("");
  const [sent, setSent] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    // No contact backend exists in this demo — show a local success state.
    setSent(true);
  };

  const sendAnother = () => {
    setName("");
    setEmail("");
    setInterest(INTERESTS[0]);
    setMessage("");
    setSent(false);
  };

  return (
    <section className="mx-auto w-full max-w-[1180px] px-6 pb-[70px] pt-24 md:px-12 md:pt-28">
      {/* Header */}
      <div className="mx-auto mb-10 max-w-[640px] text-center">
        <Eyebrow className="mb-3">WE'D LOVE TO HEAR FROM YOU</Eyebrow>
        <h1 className="font-serif text-[42px] font-normal leading-[1.05] tracking-[-0.02em] text-ink md:text-[52px]">
          Get in touch
        </h1>
        <p className="mt-3 text-[17px] leading-relaxed text-muted-200">
          Questions about a trip, a custom expedition, or just want to talk
          mountains? Fill in the form and a real guide will write back.
        </p>
      </div>

      {/* Split card */}
      <div className="grid overflow-hidden rounded-[24px] shadow-[0_30px_70px_-34px_rgba(0,0,0,0.4)] md:grid-cols-[0.85fr_1.15fr]">
        {/* Info panel */}
        <div className="relative overflow-hidden p-8 text-white md:px-10 md:py-11">
          <img
            src={ContactImg}
            alt="Travellers taking in a mountain ridge at dusk"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-[linear-gradient(150deg,rgba(19,58,69,0.92),rgba(19,58,69,0.6))]" />

          <div className="relative">
            <h2 className="mb-2 font-serif text-[30px] font-normal leading-tight">
              Wander HQ
            </h2>
            <p className="mb-9 text-[15px] leading-relaxed text-[#cfe0dc]">
              Open Mon–Fri, 9–6 MT. Trail desk answers 7 days a week.
            </p>

            <div className="flex flex-col gap-6">
              <div className="flex items-start gap-4">
                <MapPinIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary-500" />
                <div>
                  <div className={infoLabelClass}>VISIT</div>
                  <div className={infoValueClass}>
                    123 Market Street, Suite 456
                    <br />
                    Jersey City, NJ 07302
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <PhoneIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary-500" />
                <div>
                  <div className={infoLabelClass}>CALL</div>
                  <div className={infoValueClass}>+1 (201) 425-6825</div>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <EnvelopeIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary-500" />
                <div>
                  <div className={infoLabelClass}>EMAIL</div>
                  <div className={infoValueClass}>info@wanderna.com</div>
                </div>
              </div>
            </div>

            <div className="mt-9 flex gap-3">
              <button type="button" aria-label="Instagram" className={socialClass}>
                IG
              </button>
              <button type="button" aria-label="YouTube" className={socialClass}>
                YT
              </button>
              <button type="button" aria-label="Facebook" className={socialClass}>
                FB
              </button>
            </div>
          </div>
        </div>

        {/* Form / success panel */}
        <div className="bg-white p-8 md:px-11 md:pb-10 md:pt-11">
          {sent ? (
            <div className="flex flex-col items-center px-2.5 py-[50px] text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-easy/10 text-easy">
                <CheckIcon className="h-8 w-8" />
              </div>
              <h2 className="mt-[18px] font-serif text-[28px] font-normal text-ink">
                Message sent!
              </h2>
              <p className="mt-2 max-w-[360px] text-[15px] leading-relaxed text-muted">
                Thanks for reaching out — a guide will get back to you within one
                business day.
              </p>
              <button
                type="button"
                onClick={sendAnother}
                className="mt-[22px] rounded-full border border-line px-[22px] py-[11px] text-[14px] font-semibold text-ink transition-colors hover:border-secondary-400 hover:text-secondary-400"
              >
                Send another
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-4 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="c_name" className={fieldLabelClass}>
                    FULL NAME
                  </label>
                  <input
                    id="c_name"
                    name="name"
                    type="text"
                    className="wn-field"
                    placeholder="Jane Wanderer"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label htmlFor="c_email" className={fieldLabelClass}>
                    EMAIL
                  </label>
                  <input
                    id="c_email"
                    name="email"
                    type="email"
                    className="wn-field"
                    placeholder="jane@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="mb-4">
                <label htmlFor="c_topic" className={fieldLabelClass}>
                  INTERESTED IN
                </label>
                <div className="relative">
                  <select
                    id="c_topic"
                    name="interest"
                    className="wn-field cursor-pointer appearance-none pr-11"
                    value={interest}
                    onChange={(e) => setInterest(e.target.value)}
                  >
                    {INTERESTS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                  <ChevronDownIcon className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-300" />
                </div>
              </div>

              <div className="mb-5">
                <label htmlFor="c_msg" className={fieldLabelClass}>
                  MESSAGE
                </label>
                <textarea
                  id="c_msg"
                  name="message"
                  rows={5}
                  className="wn-field resize-y"
                  placeholder="Tell us where you want to wander…"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                />
              </div>

              <button
                type="submit"
                className="w-full rounded-[13px] bg-secondary-400 py-4 text-[16px] font-bold text-white transition-colors hover:bg-ink"
              >
                Send message
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

export default Contact;
